import { Button } from 'antd';
import classnames from 'classnames';
import {
  BLOCKSCAN_URLS,
  getTokenHumanReadableNameByChain,
  NetworkLogoMap,
  upperCaseNetworkType,
} from 'features/bill/lib';
import React, { FC } from 'react';
import {
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { Badge } from 'flowbite-react';
import { tokenToImageV2 } from 'shared/lib';
import { formatDollarAmount } from 'shared/lib/string';
import { toast } from 'react-toastify';

function formatWalletAddress(address: string) {
  if (!address) return '';

  // Check for ENS
  if (address.endsWith('.eth')) {
    return address;
  }

  // Check for standard Ethereum wallet format
  if (address.startsWith('0x')) {
    // Return the first 6 and last 4 characters, e.g., "0x1234...abcd"
    return address.slice(0, 6) + '...' + address.slice(-4);
  }

  // If it's neither an ENS domain nor an Ethereum address, return it unchanged.
  return address;
}

export const PayoutDetailsWidget: FC<{
  recipientName?: string;
  recipientEmail?: string;
  destinationAmount: string;
  destinationCurrency: string;
  destinationBlockchainNetwork?: string;
  destinationWalletAddress?: string;
  paymentBlockchainTxHash?: string;
  destinationBankAccount?: {
    accountBeneficiary: string;
    accountIBAN?: string;
    bankBIC?: string;
    accountNumber?: string;
    accountACHRoutingNumber?: string;
    bankName: string;
    bankAccountBeneficiaryType: string;
  };
  onPayButtonPress: () => void;
  paidBy?: string;
  showLabel?: boolean;
  className?: string;
  showPayButton?: boolean;
  paymentStatus: 'paid' | 'unpaid' | 'pending';
}> = ({
  showLabel = true,
  showPayButton = true,
  className,
  recipientName,
  destinationAmount,
  destinationCurrency,
  destinationBlockchainNetwork,
  destinationWalletAddress,
  recipientEmail,
  paymentStatus,
  destinationBankAccount,
  paidBy,
  paymentBlockchainTxHash,
  onPayButtonPress,
}) => {
  const renderPaymentStatus = () => {
    if (paymentStatus === 'paid') {
      return (
        <Badge className={'inline  !bg-slate-700 !text-emerald-400'}>
          Paid
        </Badge>
      );
    }

    if (paymentStatus === 'pending') {
      return (
        <Badge className={'inline !bg-slate-700 !text-indigo-300'}>
          Processing
        </Badge>
      );
    }

    return (
      <Badge className="inline !bg-slate-700 !text-slate-200">Unpaid</Badge>
    );
  };
  const _handleCopyParam = (val: string) => {
    navigator.clipboard.writeText(val);
    toast.success('Copied to clipboard!');
  };
  return (
    <div className={classnames('lg:col-start-3 lg:row-end-1 mb-16', className)}>
      {showLabel && <h2 className="mb-4 text-slate-100">Payout Details</h2>}
      <div className="rounded-lg bg-[#232f43] shadow-sm ring-1 ring-slate-700">
        {recipientName && (
          <div className="flex justify-between w-full flex-none gap-x-4 p-3 px-3">
            <div className="text-slate-400">Recipient</div>
            <div className="text-slate-200 flex flex-col items-end">
              <span>{recipientName}</span>
              {recipientEmail && (
                <span className="text-slate-400 text-xs">{recipientEmail}</span>
              )}
            </div>
          </div>
        )}

        <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
          <div className="text-slate-400">Amount</div>
          <div className="text-slate-200 flex items-center">
            {tokenToImageV2(destinationCurrency)}
            <span className="ml-1">
              {formatDollarAmount(destinationAmount)}{' '}
              {getTokenHumanReadableNameByChain(
                destinationCurrency,
                destinationBlockchainNetwork
              )}
            </span>
          </div>
        </div>

        {destinationBlockchainNetwork && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Blockchain</div>
            <div className="text-slate-200 flex items-center">
              <div className="mr-1 h-4 w-4">
                <img
                  src={
                    NetworkLogoMap[
                      upperCaseNetworkType(destinationBlockchainNetwork)
                    ]
                  }
                  alt=""
                  aria-hidden="true"
                />
              </div>
              {upperCaseNetworkType(destinationBlockchainNetwork)}
            </div>
          </div>
        )}

        {destinationBankAccount?.bankName && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Bank Name</div>
            <div className="text-slate-200 flex items-center">
              {destinationBankAccount.bankName}
            </div>
          </div>
        )}

        {destinationBankAccount?.accountBeneficiary && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Beneficiary Name</div>
            <div className="text-slate-200 flex items-center">
              <div className="text-slate-200 flex flex-col items-end">
                <span>{destinationBankAccount.accountBeneficiary}</span>
                {destinationBankAccount.bankAccountBeneficiaryType && (
                  <span className="text-slate-400 text-xs">
                    {destinationBankAccount.bankAccountBeneficiaryType}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {destinationBankAccount?.accountACHRoutingNumber && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Routing Number</div>
            <div className="text-slate-200 flex items-center">
              {destinationBankAccount.accountACHRoutingNumber}

              <button
                type="button"
                className="ml-2 text-slate-400"
                onClick={() =>
                  _handleCopyParam(
                    destinationBankAccount.accountACHRoutingNumber
                  )
                }>
                <ClipboardDocumentIcon className="w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {destinationBankAccount?.accountNumber && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Account Number</div>
            <div className="text-slate-200 flex items-center">
              {destinationBankAccount.accountNumber}

              <button
                type="button"
                className="ml-2 text-slate-400"
                onClick={() =>
                  _handleCopyParam(destinationBankAccount.accountNumber)
                }>
                <ClipboardDocumentIcon className="w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {destinationBankAccount?.bankBIC && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">SWIFT Code</div>
            <div className="text-slate-200 flex items-center">
              {destinationBankAccount.bankBIC}

              <button
                type="button"
                className="ml-2 text-slate-400"
                onClick={() =>
                  _handleCopyParam(destinationBankAccount.bankBIC)
                }>
                <ClipboardDocumentIcon className="w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {destinationBankAccount?.accountIBAN && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">IBAN Number</div>
            <div className="text-slate-200 flex items-center">
              {destinationBankAccount.accountIBAN}

              <button
                type="button"
                className="ml-2 text-slate-400"
                onClick={() =>
                  _handleCopyParam(destinationBankAccount.accountIBAN)
                }>
                <ClipboardDocumentIcon className="w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {destinationWalletAddress && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Wallet Address</div>
            <div className="text-slate-200">
              <div className="group relative inline-block  items-center">
                <span className="absolute text-right inset-0 z-10 cursor-text overflow-hidden transition-all duration-300 group-hover:max-w-0 group-hover:opacity-0">
                  {formatWalletAddress(destinationWalletAddress)}
                </span>
                <span className="cursor-text whitespace-nowrap opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {destinationWalletAddress}
                </span>
              </div>
            </div>
          </div>
        )}

        {paymentBlockchainTxHash && (
          <div className="flex w-full flex-none justify-between gap-x-4 border-dashed border-t border-slate-700 p-2 px-3">
            <div className="text-slate-400">Tx Hash</div>
            <div className="text-slate-200">
              <a
                href={getBlockExplorerUrl(
                  destinationBlockchainNetwork,
                  paymentBlockchainTxHash
                )}
                rel="noreferrer"
                target="_blank"
                className="hover:underline flex">
                View on Block Explorer
                <ArrowTopRightOnSquareIcon className="ml-1 w-4" />
              </a>
            </div>
          </div>
        )}

        <div className="flex flex-col w-full flex-none  gap-x-4 border-dashed border-t border-slate-700 p-3 px-3">
          <div className="flex text-slate-400  justify-between ">
            Status
            <div className="text-slate-200 flex flex-col items-end">
              {renderPaymentStatus()}

              {paidBy && (
                <span className="mt-2 text-xs text-slate-400">
                  Paid by {paidBy}
                </span>
              )}
            </div>
          </div>

          {showPayButton && paymentStatus === 'unpaid' && (
            <div className="pb-2 mt-4">
              <Button
                data-testid={`pay-bill-slide-over-btn`}
                size="large"
                type="primary"
                onClick={onPayButtonPress}
                className="bg-indigo-600 w-[100%] hover:!bg-indigo-500"
                icon={<CreditCardIcon className="w-5" />}>
                Pay {formatDollarAmount(destinationAmount)}{' '}
                {getTokenHumanReadableNameByChain(
                  destinationCurrency,
                  destinationBlockchainNetwork
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getBlockExplorerUrl = (network: string, txHash: string) => {
  const capitalizedNetwork = upperCaseNetworkType(network);
  return `${BLOCKSCAN_URLS[capitalizedNetwork]}/tx/${txHash}`;
};

// const PendingPaymentStatusRow = ({ billId }) => {
//   const { data: bill } = useBill(billId);

//   if (!bill) {
//     return null;
//   }

//   return (
//     <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
//       <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
//         {bill.receiverName}
//       </Table.Cell>

//       <Table.Cell>{bill.receiverEmail}</Table.Cell>
//       <Table.Cell>{bill.token}</Table.Cell>
//       <Table.Cell>{bill.amount}</Table.Cell>
//     </Table.Row>
//   );
// };

// const PendingPaymentStatus = ({ billId }) => {
//   const { getPendingActionByBillId } = usePendingChainActions();
//   const [modalOpened, setModalOpened] = useState(false);
//   const { isConnected } = useAccount();
//   const [showFailedMessage, setShowFailedMessage] = useState(false);
//   const [transactionSuccessful, setTransactionSuccessful] = useState(false);
//   const pendingAction = getPendingActionByBillId(billId);
//   const { approveSafeTransaction, approveSafeTransactionLoading } =
//     useApproveSafeTransaction(
//       pendingAction?.meta?.safeTxHash,
//       pendingAction?.meta?.safeContractAddress
//     );

//   const handleClose = () => {
//     if (!approveSafeTransactionLoading) {
//       setModalOpened(false);
//     }
//   };

//   const _handleApprove = () => {
//     approveSafeTransaction()
//       .then(() => {
//         setTransactionSuccessful(true);
//       })
//       .catch((err) => {
//         console.log(err);
//         setShowFailedMessage(true);
//       });
//   };

//   const renderInner = () => {
//     if (pendingAction) {
//       return (
//         <div className="flex items-center">
//           <GnosisSafeLogoWhite width={48} />
//           <span className="text-xs text-slate-300 ml-2">
//             Approval Required.
//           </span>
//           <button
//             className="ml-2 font-medium text-indigo-400 hover:text-indigo-300"
//             onClick={() => {
//               setModalOpened(true);
//             }}>
//             Approve Now
//           </button>
//         </div>
//       );
//     }

//     return (
//       <div className="flex items-center">
//         <Badge className={'inline !bg-slate-700 !text-indigo-300'}>
//           Processing
//         </Badge>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DetailSection.Item term="Status">{renderInner()}</DetailSection.Item>

//       <Modal show={modalOpened} handleClose={handleClose}>
//         <div className="p-4 min-h-[280px]">
//           <ConfirmInWalletOverlay show={approveSafeTransactionLoading} />
//           <SuccessConfirmation
//             title={'Success!'}
//             description={'You successfully approved transaction.'}
//             onClose={handleClose}
//             show={transactionSuccessful}
//           />
//           <FailedPayment
//             title={'Transaction Approve Failed'}
//             onClose={handleClose}
//             show={showFailedMessage}
//           />

//           <h2 className="text-base font-semibold leading-7 text-white">
//             Gnosis Safe Transaction Summary
//           </h2>
//           <p className="mt-1 mb-4 text-sm leading-6 text-gray-400">
//             An overview of bills that require approval.
//           </p>

//           {!!pendingAction && (
//             <Table>
//               <Table.Head>
//                 <Table.HeadCell>Receiver name</Table.HeadCell>
//                 <Table.HeadCell>Receiver Email</Table.HeadCell>
//                 <Table.HeadCell>Currency</Table.HeadCell>
//                 <Table.HeadCell>Amount</Table.HeadCell>
//               </Table.Head>
//               <Table.Body className="divide-y">
//                 {pendingAction.billIds.map((billId) => {
//                   return (
//                     <PendingPaymentStatusRow key={billId} billId={billId} />
//                   );
//                 })}
//               </Table.Body>
//             </Table>
//           )}

//           <span className="flex items-center justify-end mt-6 max-w-[640px] text-sm text-left text-gray-400">
//             <ExclamationTriangleIcon className="text-amber-400 mr-4 inline h-6 w-6" />
//             Ensure you're using the same chain in wallet connect previously
//             selected by you or your team for initiating a multisig transaction.
//           </span>

//           <div className="mt-6 flex flex-row-reverse gap-x-4 ">
//             {isConnected ? (
//               <ButtonF
//                 size="sm"
//                 color="indigo"
//                 className="bg-indigo-600 w-48"
//                 onClick={_handleApprove}>
//                 Approve transaction
//               </ButtonF>
//             ) : (
//               <div className="rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
//                 <ConnectButton />
//               </div>
//             )}

//             <ButtonF
//               size="sm"
//               color="gray"
//               className="w-48"
//               onClick={handleClose}>
//               Cancel
//             </ButtonF>
//           </div>
//         </div>
//       </Modal>
//     </>
//   );
// };
